import ArrowBack from "@mui/icons-material/ArrowBack";
import { Button, Stack, Tab, Tabs, Typography } from "@mui/material";
import { noop } from "lodash-es";
import { Navigate, Link as RouterLink, useSearchParams } from "react-router";
import { ContentFeed } from "../../modules/dashboard/components/recently-edited/content-feed";
import { useContentTabs } from "../../modules/dashboard/components/recently-edited/content-tabs";
import { DraggableCommunications } from "../../modules/dashboard/components/recently-edited/feed-drag-and-drop";
import { useContentSearch } from "../../modules/dashboard/hooks/use-content-search";
import { Page } from "../../modules/layout/components/page";
import { PageContent } from "../../modules/layout/components/page-content";
import { SearchField } from "../../modules/search/search-field";
import { useFlags } from "../../server/optimizely";

import type { ContentTab } from "../../modules/dashboard/components/recently-edited/content-tabs";
import type { PagedFeed } from "../../modules/dashboard/hooks/use-paged-feed";
import type { FeedItem } from "../../modules/dashboard/hooks/use-recently-edited";
import type { FC } from "react";

/**
 * What's found, as the Dashboard's Recently edited feed shows it, with its Communications to drag to
 * another account's home page.
 */
const Results: FC<{ feed: PagedFeed<FeedItem> }> = ({ feed }) => (
  <DraggableCommunications>
    <ContentFeed
      feed={feed}
      // Nothing here counts what's archived or duplicated.
      onChanged={noop}
      empty={<Typography>Nothing found.</Typography>}
      errorMessage="Search failed."
      analyticsId="search-results"
    />
  </DraggableCommunications>
);

/** Where the Dashboard's search lands. */
const SearchPage: FC = () => {
  const {
    flags: { cha_4551_dashboard_2026 },
    flagsLoaded,
  } = useFlags("cha_4551_dashboard_2026");
  const [searchParams, setSearchParams] = useSearchParams();
  const q = (searchParams.get("q") ?? "").trim();
  const tabs = useContentTabs();
  // A kind this account can't see, or none, is All; so is any before the first search.
  const current = (q && tabs.find(({ value }) => value === searchParams.get("type"))) || tabs[0]!;
  const tab = current.value;
  const pickTab = (value: ContentTab) =>
    setSearchParams(
      (params) => {
        if (value === "all") params.delete("type");
        else params.set("type", value);
        return params;
      },
      { replace: true },
    );
  // Nothing before the first search.
  const feed = useContentSearch(q, tab === "all" ? undefined : tab);
  const searching = feed.reloading || (feed.items === null && !feed.error);
  // Its own history entry, so Back returns to the search before. The first searches everything.
  const searchAgain = (value: string) => {
    const next = value.trim();
    if (!next) return;
    // The same search again loads afresh, as after a failure or edits elsewhere; once at a time.
    if (next === q) {
      if (!searching) feed.reload();
      return;
    }
    setSearchParams((params) => {
      params.set("q", next);
      if (!q) params.delete("type");
      return params;
    });
  };

  if (!flagsLoaded) return null;
  if (!cha_4551_dashboard_2026) return <Navigate replace to="/" />;

  return (
    <Page title="Search | ChangeEngine">
      <PageContent maxWidth="lg" sx={{ mt: 4 }}>
        <Stack spacing={2}>
          <Button
            component={RouterLink}
            to="/"
            variant="text"
            size="small"
            startIcon={<ArrowBack />}
            sx={{ alignSelf: "flex-start" }}
            data-analytics-id="search-back-to-dashboard"
          >
            Back Home
          </Button>
          <Typography variant="h5" component="h1">
            {q ? `Search results for ${q}` : "Search"}
          </Typography>
          <SearchField
            // Shows the query again when it changes from outside, as on Back.
            key={q}
            label={current.search}
            title={current.search}
            defaultValue={q}
            onSubmit={searchAgain}
            sx={{ maxWidth: 600 }}
          />
          {q && (
            <>
              <Tabs
                value={tab}
                onChange={(_, value: ContentTab) => pickTab(value)}
                aria-label="Search results"
                variant="scrollable"
                scrollButtons={false}
              >
                {tabs.map(({ value, label }) => (
                  <Tab
                    key={value}
                    value={value}
                    label={label}
                    data-analytics-id={`search-tab-${label.toLowerCase().replaceAll(" ", "-")}`}
                  />
                ))}
              </Tabs>
              <Results feed={feed} />
            </>
          )}
        </Stack>
      </PageContent>
    </Page>
  );
};

export default SearchPage;
